// src/components/WeeklySummary/WeeklySummary.js
import React, { useMemo, useEffect, useRef } from 'react';
import { useTranslation } from '../../i18n';
import './../styles/WeeklySummary.css';

import runningIcon from '../../assets/icons/running.png';
import cyclingIcon from '../../assets/icons/cycling.png';
import swimIcon from '../../assets/icons/swim.png';
import strengthIcon from '../../assets/icons/strength.png';
import tzoneLogo from '../../assets/img/logo.png';

const activityIcons = {
  correr: runningIcon,
  ciclismo: cyclingIcon,
  nadar: swimIcon,
  fuerza: strengthIcon,
};

const COLORS = {
  meet: '#2ecc71',
  below: '#e74c3c',
  above: '#f1c40f',
  neutral: '#333',
};

// =========================
// Debug (logs)
// =========================
// En DEV loguea por defecto.
// Para forzar logs en PROD: window.__WS_DEBUG__=true o localStorage.setItem('WS_DEBUG','1')
const WS_DEBUG = (() => {
  try {
    if (typeof window !== 'undefined') {
      if (window.__WS_DEBUG__ === true) return true;
      if (window.localStorage && window.localStorage.getItem('WS_DEBUG') === '1') return true;
    }
  } catch (_e) {
    /* noop */
  }
  return process.env.REACT_APP_WS_DEBUG === '1';
})();

const wsLog = (...args) => {
  if (WS_DEBUG) console.log(...args);
};
const wsWarn = (...args) => {
  if (WS_DEBUG) console.warn(...args);
};

const TOLERANCE = 0.15;
const clampPct = (v) => Math.max(0, Math.min(100, Number(Number(v || 0).toFixed(1))));

/* =========================
   Helpers robustos
========================= */
const isFiniteNumber = (n) => typeof n === 'number' && Number.isFinite(n);

// ✅ “primer valor definido” (para listas de candidatos)
const firstDefined = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== '');

// ✅ toNum: devuelve NaN si no parsea (para que puedas distinguir “no hay data” vs 0 real)
const toNum = (v) => {
  if (v == null) return NaN;
  if (isFiniteNumber(v)) return v;

  if (typeof v === 'string') {
    const cleaned = v.replace(/[^\d.,-]/g, '').trim();
    if (!cleaned) return NaN;
    const s = cleaned.replace(',', '.');
    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
  }

  const s = String(v).trim().replace(',', '.');
  if (!s) return NaN;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

// ✅ toNum0: siempre número (0 si no parsea)
const toNum0 = (v) => {
  const n = toNum(v);
  return Number.isFinite(n) ? n : 0;
};

// "HH:MM:SS" or "MM:SS" -> minutos
const parseTimeStringToMinutes = (s) => {
  const t = String(s || '').trim();
  if (!t) return 0;

  let m = t.match(/^(\d{1,3}):(\d{2}):(\d{2})$/);
  if (m) {
    const hh = Number(m[1]) || 0;
    const mm = Number(m[2]) || 0;
    const ss = Number(m[3]) || 0;
    return hh * 60 + mm + ss / 60;
  }

  m = t.match(/^(\d{1,4}):(\d{2})$/);
  if (m) {
    const mm = Number(m[1]) || 0;
    const ss = Number(m[2]) || 0;
    return mm + ss / 60;
  }

  return 0;
};

/**
 * minutos “smart”:
 * - string hh:mm:ss => parse
 * - ms grandes => /60000
 * - sec típicos => /60
 * - minutos => 그대로
 */
const minutesFromAny = (v) => {
  if (v == null) return 0;

  if (typeof v === 'string') {
    const parsed = parseTimeStringToMinutes(v);
    if (parsed > 0) return parsed;
    const n = toNum(v);
    if (!Number.isFinite(n)) return 0;
    v = n;
  }

  const n = toNum(v);
  if (!Number.isFinite(n) || n <= 0) return 0;

  if (n >= 200000) return n / 60000; // ms
  if (n > 1000) return n / 60; // sec
  return n; // min
};

/**
 * distancia km “smart”
 */
const kmFromAny = (type, v) => {
  if (v == null) return 0;

  if (typeof v === 'string') {
    const s = v.trim().toLowerCase().replace(',', '.');
    const num = parseFloat(s);
    if (!Number.isFinite(num)) return 0;

    if (s.includes(' km')) return num;
    if (s.includes(' m')) return num / 1000;

    v = num;
  }

  const n = toNum(v);
  if (!Number.isFinite(n) || n <= 0) return 0;

  // swim suele venir en metros
  if (type === 'nadar' && n >= 50) return n / 1000;
  if (n > 2000) return n / 1000;

  return n;
};

const formatNumber = (value) => (value !== undefined && !isNaN(value) ? parseFloat(value).toFixed(2) : '0.00');

const formatTime = (minutes) => {
  const min = Number(minutes) || 0;
  if (min <= 0 || Number.isNaN(min)) return '00:00:00';
  const totalSeconds = Math.round(min * 60);
  const h = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
  const s = String(totalSeconds % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

const colorForPlanVsReal = (real, plan) => {
  const r = Number(real) || 0;
  const p = Number(plan) || 0;
  if (p <= 0) return COLORS.neutral;
  if (r >= p * (1 + TOLERANCE)) return COLORS.above;
  if (r >= p * (1 - TOLERANCE)) return COLORS.meet;
  return COLORS.below;
};

/* =========================
   Read planned/completed
========================= */
const pickFirst = (obj, keys) => {
  if (!obj) return undefined;
  if (!Array.isArray(keys)) return undefined; // evita "keys is not iterable"
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
};

// búsqueda “flexible” por nombre de llave (para casos raros: Duration, Duración, etc.)
const pickByIncludes = (obj, needles = []) => {
  if (!obj || typeof obj !== 'object') return undefined;
  const keys = Object.keys(obj);
  for (const k of keys) {
    const lk = k.toLowerCase();
    if (needles.some((n) => lk.includes(n))) {
      const v = obj[k];
      if (v !== undefined && v !== null && v !== '') return v;
    }
  }
  return undefined;
};

const readPlanned = (planned = {}, metric) => {
  switch (metric) {
    case 'sessions': {
      const v = pickFirst(planned, ['totalActivities', 'sessions', 'count', 'total', 'totalSessions']);
      return Number(v) || 0;
    }
    case 'distance': {
      return pickFirst(planned, [
        'distancia',
        'distance',
        'distanceKm',
        'dist',
        'km',
        'totalDistance',
        'total_distance',
      ]);
    }
    case 'duration': {
      return (
        pickFirst(planned, [
          'duracion',
          'duration',
          'durationMin',
          'minutes',
          'totalTime',
          'total_timer_time',
          'totalTimerTime',
        ]) ?? pickByIncludes(planned, ['dur', 'time'])
      );
    }
    case 'calories': {
      const v =
        pickFirst(planned, [
          'calorias',
          'calories',
          'kcal',
          'energy',
          'totalCalories',
          'total_calories',
          'activeCalories',
          'active_calories',
          'calorie',
          'caloriesBurned',
          'calories_burned',
          'activeEnergy',
          'active_energy',
          'total_energy',
          'energy_kcal',
        ]) ?? pickByIncludes(planned, ['cal', 'kcal', 'energy']);
      return toNum0(v);
    }
    case 'elevation': {
      const v = pickFirst(planned, ['desnivel_positivo', 'elevation', 'elevGain', 'elevationGain', 'totalAscent', 'ascent']);
      return toNum0(v);
    }
    case 'tss': {
      const v = pickFirst(planned, ['tss', 'hrTss', 'tss_hr', 'TSS']);
      return toNum0(v);
    }
    default:
      return 0;
  }
};

const readCompleted = (completed = {}, metric) => {
  switch (metric) {
    case 'sessions': {
      const v = pickFirst(completed, ['totalActivities', 'sessions', 'count', 'total', 'totalSessions']);
      return Number(v) || 0;
    }
    case 'distance': {
      return pickFirst(completed, [
        'distancia',
        'distance',
        'distanceKm',
        'dist',
        'km',
        'totalDistance',
        'total_distance',
      ]);
    }
    case 'duration': {
      return (
        pickFirst(completed, [
          'duracion',
          'duration',
          'durationMin',
          'minutes',
          'totalTime',
          'total_timer_time',
          'totalTimerTime',
          'total_elapsed_time',
          'totalElapsedTime',
        ]) ?? pickByIncludes(completed, ['dur', 'time'])
      );
    }
    case 'calories': {
      const v =
        pickFirst(completed, [
          'calorias',
          'calories',
          'kcal',
          'energy',
          'totalCalories',
          'total_calories',
          'activeCalories',
          'active_calories',
          'calorie',
          'caloriesBurned',
          'calories_burned',
          'activeEnergy',
          'active_energy',
          'total_energy',
          'energy_kcal',
        ]) ?? pickByIncludes(completed, ['cal', 'kcal', 'energy']);
      return toNum0(v);
    }
    case 'elevation': {
      const v = pickFirst(completed, ['desnivel_positivo', 'elevation', 'elevGain', 'elevationGain', 'totalAscent', 'ascent']);
      return toNum0(v);
    }
    case 'tss': {
      const v = pickFirst(completed, ['tss', 'hrTss', 'tss_hr', 'TSS']);
      return toNum0(v);
    }
    default:
      return 0;
  }
};

/* =========================
   Fallback desde eventos del calendario
   (IMPORTANTE: SOLO cuenta COMPLETED REAL)
========================= */
const normalizeTypeKey = (raw) => {
  const t = String(raw || '').toLowerCase().trim();

  if (!t) return '';
  if (t.includes('run') || t.includes('correr') || t.includes('running') || t.includes('trote') || t.includes('trail'))
    return 'correr';
  if (t.includes('bike') || t.includes('cycling') || t.includes('cicl') || t.includes('mtb') || t.includes('rodillo'))
    return 'ciclismo';
  if (t.includes('swim') || t.includes('swimming') || t.includes('nadar') || t.includes('natacion') || t.includes('natación'))
    return 'nadar';
  if (t.includes('strength') || t.includes('fuerza') || t.includes('gym') || t.includes('pesas') || t.includes('fit'))
    return 'fuerza';

  return '';
};

const unwrapActivityLike = (item) => {
  const ext = item?.extendedProps;
  if (ext && typeof ext === 'object') {
    return {
      type: ext.type ?? item?.type ?? item?.title,
      planned: ext.planned || {},
      completed: ext.completed || {},
      metrics: ext.metrics || {},
      ...item,
      ...ext,
    };
  }
  return item || {};
};

// ✅ Regla: "completed" existe siempre, pero solo cuenta si trae evidencia real
const isRealCompleted = (c) => {
  if (!c) return false;

  const duration = String(c.duration || '').trim();
  const hasDuration = Boolean(duration && duration !== '00:00:00');
  const hasDistance = toNum0(c.distance) > 0;
  const hasCalories = toNum0(c.calories) > 0;

  const fileId = c.fileId;
  const hasFile = Boolean(fileId && String(fileId).trim() !== '' && String(fileId) !== 'undefined');

  return hasDuration || hasDistance || hasCalories || hasFile;
};

// ✅ Solo fuentes “completed” (NO planned)
const readActDistanceRawCompleted = (aLike) => {
  const c = aLike?.completed || {};
  const m = aLike?.metrics || aLike?.completed?.metrics || {};
  const s = aLike?.summary || aLike?.completed?.summary || {};

  return firstDefined(
    c?.distancia, c?.distance, c?.distanceKm, c?.distance_km, c?.total_distance, c?.totalDistance,
    m?.distancia, m?.distance, m?.distanceKm, m?.distance_km, m?.total_distance, m?.totalDistance,
    s?.distancia, s?.distance, s?.distanceKm, s?.distance_km,
    aLike?.distancia, aLike?.distance, aLike?.distanceKm, aLike?.distance_km,
    aLike?.dist
  );
};

const readActDurationRawCompleted = (aLike) => {
  const c = aLike?.completed || {};
  const m = aLike?.metrics || aLike?.completed?.metrics || {};
  const s = aLike?.summary || aLike?.completed?.summary || {};

  return firstDefined(
    c?.duracion, c?.duration, c?.durationSec, c?.duration_sec, c?.movingTime, c?.elapsedTime, c?.moving_time, c?.elapsed_time,
    m?.duracion, m?.duration, m?.durationSec, m?.duration_sec, m?.movingTime, m?.elapsedTime,
    s?.duracion, s?.duration, s?.durationSec, s?.duration_sec,
    aLike?.duracion, aLike?.duration, aLike?.durationSec, aLike?.duration_sec, aLike?.movingTime, aLike?.elapsedTime,
    aLike?.tmin
  );
};

const readActTssCompleted = (aLike) => {
  const c = aLike?.completed || {};
  const m = aLike?.metrics || aLike?.completed?.metrics || {};
  const s = aLike?.summary || aLike?.completed?.summary || {};

  return firstDefined(
    c?.tss, c?.TSS,
    m?.tss, m?.TSS,
    s?.tss, s?.TSS,
    aLike?.tss, aLike?.TSS
  );
};

const readActCaloriesCompleted = (aLike) => {
  const candidates = [
    ['completed.calories', toNum(aLike?.completed?.calories)],
    ['completed.calorias', toNum(aLike?.completed?.calorias)],
    ['completed.kcal', toNum(aLike?.completed?.kcal)],
    ['completed.energyKcal', toNum(aLike?.completed?.energyKcal)],
    ['completed.energy_kcal', toNum(aLike?.completed?.energy_kcal)],
    ['completed.energy', toNum(aLike?.completed?.energy)],
    ['kcal', toNum(aLike?.kcal)],
    ['calories', toNum(aLike?.calories)],
    ['calorias', toNum(aLike?.calorias)],
  ];

  const picked = candidates.find(([, v]) => Number.isFinite(v) && v > 0);
  const val = picked ? picked[1] : 0;

  if (WS_DEBUG) {
    const hasEvidence = Boolean(aLike?.completed?.fileId || aLike?.fileId || Object.keys(aLike?.metrics || {}).length);
    wsLog('[WS][caloriesCompleted] activity', {
      id: String(aLike?._id || aLike?.id || ''),
      type: String(aLike?.type || aLike?.sport || ''),
      fileId: aLike?.completed?.fileId || aLike?.fileId || null,
      picked: picked ? picked[0] : null,
      val,
    });
    if (hasEvidence && (!val || val === 0)) {
      wsWarn('[WS][caloriesCompleted] ZERO with evidence', {
        id: String(aLike?._id || aLike?.id || ''),
        type: String(aLike?.type || aLike?.sport || ''),
        fileId: aLike?.completed?.fileId || aLike?.fileId || null,
      });
    }
  }

  return val;
};

const readActElevGainCompleted = (aLike) => {
  const c = aLike?.completed || {};
  const m = aLike?.metrics || aLike?.completed?.metrics || {};
  const s = aLike?.summary || aLike?.completed?.summary || {};

  return firstDefined(
    c?.desnivel_positivo, c?.elevGain, c?.elevationGain, c?.elevation_gain, c?.ascent, c?.totalAscent,
    m?.desnivel_positivo, m?.elevGain, m?.elevationGain, m?.ascent, m?.totalAscent,
    s?.desnivel_positivo, s?.elevGain, s?.elevationGain,
    aLike?.desnivel_positivo, aLike?.elevGain, aLike?.elevationGain, aLike?.elevation_gain, aLike?.ascent, aLike?.totalAscent,
    aLike?.elev
  );
};

const computeCompletedFromActivities = (weekActivities) => {
  const acts = Array.isArray(weekActivities) ? weekActivities : [];
  const out = {};

  for (const item of acts) {
    const aLike = unwrapActivityLike(item);

    const type = normalizeTypeKey(
      aLike?.type ||
        aLike?.sport ||
        aLike?.activityType ||
        aLike?.discipline ||
        aLike?.category ||
        aLike?.completed?.sport ||
        aLike?.title ||
        ''
    );

    if (!type) continue;

    const cObj = aLike?.completed || {};
    if (!isRealCompleted(cObj)) {
      if (WS_DEBUG && (type === 'fuerza' || type === 'nadar')) {
        wsLog('[WS][skip-not-completed]', {
          type,
          id: aLike?.id || aLike?._id,
          title: aLike?.title,
          fileId: cObj?.fileId,
          duration: cObj?.duration,
          distance: cObj?.distance,
          calories: cObj?.calories,
          tss: aLike?.tss,
        });
      }
      continue;
    }

    if (!out[type]) {
      out[type] = { sessions: 0, distanceKm: 0, durationMin: 0, calories: 0, elevation: 0, tss: 0 };
    }

    out[type].sessions += 1;

    const distRaw = readActDistanceRawCompleted(aLike);
    const km = kmFromAny(type, distRaw);

    const durRaw = readActDurationRawCompleted(aLike);
    const min = minutesFromAny(durRaw);

    const cal = readActCaloriesCompleted(aLike);
    const tss = toNum0(readActTssCompleted(aLike));
    const elev = toNum0(readActElevGainCompleted(aLike));

    if (WS_DEBUG && (type === 'fuerza' || type === 'nadar')) {
      wsLog('[WS][agg COMPLETED]', {
        type,
        id: aLike?.id || aLike?._id,
        title: aLike?.title,
        distRaw,
        km,
        durRaw,
        min,
        cal,
        tss,
        elev,
        fileId: cObj?.fileId || null,
      });
    }

    out[type].distanceKm += km;
    out[type].durationMin += min;
    out[type].tss += tss;
    out[type].calories += toNum0(cal);
    out[type].elevation += elev;
  }

  return out;
};

/**
 * Cumplimiento por sesiones + fallback a actividades si ws.completed viene 0
 */
function computeCompletionRate(ws, completedFromActsByType) {
  if (!ws) return 0;

  let planned = Number(
    ws?.planned?.totalActivities ?? ws?.planned?.sessions ?? ws?.plannedSessions ?? ws?.planned?.totalSessions ?? 0
  );

  let completed = Number(
    ws?.completed?.totalActivities ??
      ws?.completed?.sessions ??
      ws?.completedSessions ??
      ws?.completed?.totalSessions ??
      0
  );

  if (completed <= 0 && completedFromActsByType) {
    completed = Object.values(completedFromActsByType).reduce((acc, v) => acc + (v?.sessions || 0), 0);
  }

  if (planned === 0 && completed === 0 && ws?.byType) {
    for (const key of Object.keys(ws.byType)) {
      const p = Number(
        ws.byType[key]?.planned?.totalActivities ??
          ws.byType[key]?.planned?.sessions ??
          ws.byType[key]?.planned?.totalSessions ??
          0
      );
      const c = Number(
        ws.byType[key]?.completed?.totalActivities ??
          ws.byType[key]?.completed?.sessions ??
          ws.byType[key]?.completed?.totalSessions ??
          0
      );
      planned += p;
      completed += c;
    }
  }

  if (planned <= 0) return 0;

  const pct = (Math.min(completed, planned) / planned) * 100;
  return clampPct(pct);
}

/* =========================
   Component
========================= */
const WeeklySummary = ({ weeklySummary, weekActivities = null }) => {
  const { t } = useTranslation();

  // ✅ Si weekActivities NO es array => modo estricto (NO fallback + NO sticky)
  const fallbackEnabled = Array.isArray(weekActivities);

  // ✅ 1) calculo raw (solo completed real) - solo si fallback está habilitado
  const completedFromActsByTypeRaw = useMemo(() => {
    if (!fallbackEnabled) return {};
    return computeCompletedFromActivities(weekActivities);
  }, [fallbackEnabled, weekActivities]);

  // ✅ 2) sticky: no dejar que se degrade a 0 con refetch (SOLO si fallbackEnabled)
  const lastGoodAggRef = useRef({});

  useEffect(() => {
    if (!fallbackEnabled) {
      lastGoodAggRef.current = {};
    }
  }, [fallbackEnabled]);

  const keepPrevIfZero = (nextVal, prevVal) => {
    const n = Number(nextVal);
    if (Number.isFinite(n) && n > 0) return n;
    const p = Number(prevVal);
    return Number.isFinite(p) ? p : 0;
  };

  const completedFromActsByType = useMemo(() => {
    if (!fallbackEnabled) return {};

    const raw = completedFromActsByTypeRaw || {};
    const last = lastGoodAggRef.current || {};

    if (!raw || Object.keys(raw).length === 0) return last;

    const merged = { ...last };

    for (const [type, v] of Object.entries(raw)) {
      const prev = merged[type] || {};
      merged[type] = {
        sessions: keepPrevIfZero(v?.sessions, prev?.sessions),
        distanceKm: keepPrevIfZero(v?.distanceKm, prev?.distanceKm),
        durationMin: keepPrevIfZero(v?.durationMin, prev?.durationMin),
        tss: keepPrevIfZero(v?.tss, prev?.tss),
        elevation: keepPrevIfZero(v?.elevation, prev?.elevation),
        calories: keepPrevIfZero(v?.calories, prev?.calories),
      };
    }

    return merged;
  }, [fallbackEnabled, completedFromActsByTypeRaw]);

  useEffect(() => {
    if (!fallbackEnabled) return;
    lastGoodAggRef.current = completedFromActsByType;
  }, [fallbackEnabled, completedFromActsByType]);

  useEffect(() => {
    if (!WS_DEBUG) return;

    console.log(
      '[WeeklySummary] weekActivities len:',
      Array.isArray(weekActivities) ? weekActivities.length : weekActivities
    );
    console.log('[WeeklySummary][fallbackEnabled]', fallbackEnabled);
    console.log('[WeeklySummary][fallback RAW]', completedFromActsByTypeRaw);
    console.log('[WeeklySummary][fallback STICKY]', completedFromActsByType);
    console.log('[WeeklySummary][fallback swim]', completedFromActsByType?.nadar);
    console.log('[WeeklySummary][fallback strength]', completedFromActsByType?.fuerza);
  }, [weekActivities, fallbackEnabled, completedFromActsByTypeRaw, completedFromActsByType]);

  if (!weeklySummary?.byType) return null;

  const metrics = [
    { key: 'sessions', label: t('weeklySummary.sessions') },
    { key: 'distance', label: t('weeklySummary.distance') },
    { key: 'duration', label: t('weeklySummary.duration') },
    { key: 'calories', label: t('weeklySummary.calories') },
    { key: 'elevation', label: t('weeklySummary.elevation') },
    { key: 'tss', label: t('weeklySummary.tss') },
  ];

  const totals = {
    sessions: 0,
    distanceKm: 0,
    durationMin: 0,
    calories: 0,
    elevation: 0,
    tss: 0,
  };

  const completionRate = computeCompletionRate(weeklySummary, fallbackEnabled ? completedFromActsByType : null);
  const completionText = `${completionRate.toFixed(1)}%`;

  return (
    <div className="weekly-summary-container">
      <div className="weekly-summary-title-bar">
        <img src={tzoneLogo} alt="TZone" className="tzone-summary-logo" />
        <div className="weekly-summary-title-text">
          <strong>{t('weeklySummary.title')}</strong>
          <div className="weekly-summary-progress-bar">
            <div className="fill" style={{ width: `${completionRate}%` }}>
              {completionText}
            </div>
          </div>
        </div>
      </div>

      <h3 className="weekly-summary-subtitle">{t('weeklySummary.subtitle')}</h3>

      <table className="weekly-summary-table">
        <thead>
          <tr>
            <th>{t('weeklySummary.metric')}</th>
            {Object.keys(weeklySummary.byType).map((type) => (
              <th key={type} colSpan="2" className="weekly-icon-col">
                <img src={activityIcons[type]} alt={type} title={type} width="24" />
              </th>
            ))}
            <th>{t('laps.total')}</th>
          </tr>
        </thead>

        <tbody>
          {metrics.map((metric) => (
            <tr key={metric.key}>
              <td>{metric.label}</td>

              {Object.entries(weeklySummary.byType).map(([type, values]) => {
                const planned = values?.planned || {};
                const completed = values?.completed || {};
                const actAgg = fallbackEnabled ? (completedFromActsByType?.[type] || null) : null;

                let planVal = '';
                let realVal = '';
                let color = COLORS.neutral;

                switch (metric.key) {
                  case 'sessions': {
                    const p = readPlanned(planned, 'sessions');
                    const cBackend = readCompleted(completed, 'sessions');
                    const c = cBackend > 0 ? cBackend : actAgg?.sessions || 0;

                    planVal = p;
                    realVal = c;
                    totals.sessions += c;
                    break;
                  }

                  case 'distance': {
                    const pRaw = readPlanned(planned, 'distance');
                    const cRaw = readCompleted(completed, 'distance');

                    const pKm = kmFromAny(type, pRaw);
                    const cKmBackend = kmFromAny(type, cRaw);
                    const fbKm = actAgg?.distanceKm || 0;

                    const cKm = cKmBackend > 0 ? cKmBackend : fbKm;

                    planVal = formatNumber(pKm);
                    realVal = formatNumber(cKm);
                    color = colorForPlanVsReal(cKm, pKm);

                    totals.distanceKm += cKm;
                    break;
                  }

                  case 'duration': {
                    const pRaw = readPlanned(planned, 'duration');
                    const cRaw = readCompleted(completed, 'duration');

                    const pMin = minutesFromAny(pRaw);
                    const cMinBackend = minutesFromAny(cRaw);
                    const fbMin = actAgg?.durationMin || 0;

                    const cMin = cMinBackend > 0 ? cMinBackend : fbMin;

                    planVal = pMin > 0 ? formatTime(pMin) : '';
                    realVal = cMin > 0 ? formatTime(cMin) : '';

                    color = colorForPlanVsReal(cMin, pMin);
                    totals.durationMin += cMin;
                    break;
                  }

                  case 'calories': {
                    const p = readPlanned(planned, 'calories');
                    const cBackend = readCompleted(completed, 'calories');
                    const fbCal = actAgg?.calories || 0;

                    const c = cBackend > 0 ? cBackend : fbCal;

                    planVal = p;
                    realVal = c;
                    totals.calories += c;
                    break;
                  }

                  case 'elevation': {
                    const p = readPlanned(planned, 'elevation');
                    const cBackend = readCompleted(completed, 'elevation');
                    const c = cBackend > 0 ? cBackend : actAgg?.elevation || 0;

                    planVal = p;
                    realVal = c;
                    totals.elevation += c;
                    break;
                  }

                  case 'tss': {
                    const p = readPlanned(planned, 'tss');
                    const cBackend = readCompleted(completed, 'tss');
                    const c = cBackend > 0 ? cBackend : actAgg?.tss || 0;

                    planVal = formatNumber(p);
                    realVal = formatNumber(c);
                    totals.tss += c;
                    break;
                  }

                  default:
                    break;
                }

                return (
                  <React.Fragment key={type + metric.key}>
                    <td>{planVal}</td>
                    <td className="summary-completed-column" style={{ fontWeight: 'bold', color }}>
                      {realVal}
                    </td>
                  </React.Fragment>
                );
              })}

              <td>
                {metric.key === 'sessions'
                  ? totals.sessions
                  : metric.key === 'distance'
                  ? `${totals.distanceKm.toFixed(2)} km`
                  : metric.key === 'duration'
                  ? formatTime(totals.durationMin)
                  : metric.key === 'calories'
                  ? totals.calories
                  : metric.key === 'elevation'
                  ? `${totals.elevation} m`
                  : metric.key === 'tss'
                  ? totals.tss.toFixed(2)
                  : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default WeeklySummary;